import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { getMISReport } from "../../core/api/reportService";
import { getApiErrorMessage } from "../../core/utils/apiError";

const today = () => new Date().toISOString().split("T")[0];
const DEFAULT_FILTERS = { from: today(), to: today() };
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
const num = (v) => Number(v || 0).toLocaleString("en-IN");

const StatCard = ({ label, value, color, sub }) => (
  <div className="col-6 col-md-3 mb-3">
    <div className={`card border-${color} h-100`}>
      <div className="card-body text-center py-3">
        <div className={`text-${color} fw-bold`} style={{ fontSize: "1.4rem" }}>{value}</div>
        <div className="text-muted small mt-1">{label}</div>
        {sub && <div className="text-muted" style={{ fontSize: "0.72rem" }}>{sub}</div>}
      </div>
    </div>
  </div>
);

const MISReport = () => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runReport(); }, []);

  const runReport = async (f = filters) => {
    setLoading(true);
    try {
      const res = await getMISReport(f);
      setReport(res.data || {});
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to load MIS report."), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    runReport(DEFAULT_FILTERS);
  };

  const r = report || {};

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>MIS Report</h4><h6>Management Information Summary</h6></div>
        </div>

        {/* Filter */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">From</label>
                <input type="date" className="form-control form-control-sm" value={filters.from}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1">To</label>
                <input type="date" className="form-control form-control-sm" value={filters.to}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={() => runReport()} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-dark" onClick={handleReset} disabled={loading}>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {report && (
          <>
            <h6 className="text-muted mb-2 mt-1">Revenue & Collection</h6>
            <div className="row mb-3">
              <StatCard label="Total Revenue" value={fmt(r.totalRevenue)} color="primary" />
              <StatCard label="Gross Revenue" value={fmt(r.grossRevenue)} color="primary" />
              <StatCard label="Amount Collected" value={fmt(r.totalCollected)} color="success" />
              <StatCard label="Net Profit" value={fmt(r.netProfit)} color={r.netProfit < 0 ? "danger" : "success"} />
            </div>

            <h6 className="text-muted mb-2">Purchases & Expenses</h6>
            <div className="row mb-3">
              <StatCard label="Total Purchases" value={fmt(r.totalPurchases)} color="warning" />
              <StatCard label="Total Expenses" value={fmt(r.totalExpenses)} color="danger" />
            </div>

            <h6 className="text-muted mb-2">Receivables & Payables</h6>
            <div className="row mb-3">
              <StatCard label="Outstanding (Receivables)" value={fmt(r.outstanding)} color="danger" />
              <StatCard label="Outstanding (Payables)" value={fmt(r.totalPayables)} color="warning" />
            </div>

            <h6 className="text-muted mb-2">Appointments</h6>
            <div className="row mb-3">
              <StatCard label="Total Appointments" value={num(r.totalAppointments)} color="primary" />
            </div>
          </>
        )}

        {!report && !loading && (
          <div className="card text-center py-5 text-muted">
            <p>Select a date range and click <strong>Search</strong> to view the MIS summary.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MISReport;
